import { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { baseURL } from "../utils/common";

const ITEMS_URL = `${baseURL}/shop/admin/items/`;
const ITEM_CREATE_URL = `${baseURL}/shop/admin/item/create/`;
const ITEM_UPDATE_URL = (id) => `${baseURL}/shop/admin/item/${id}/update/`;
const ITEM_DELETE_URL = (id) => `${baseURL}/shop/admin/item/${id}/delete/`;

const ITEM_FILE_CREATE_URL = (id) => `${baseURL}/shop/admin/item/${id}/file/create/`;
const ITEM_FILE_DELETE_URL = (id) => `${baseURL}/shop/admin/item/file/${id}/delete/`;

const itemFieldOrder = [
    "sub_category_id",
    "image",
    "clear_image",
    "position",
    "ru_name",
    "slug",
    "price",
    "opt_price",
    "name_ru",
    "name_en",
    "name_kk",
    "short_description_ru",
    "short_description_en",
    "short_description_kk",
    "vision_ru",
    "vision_en",
    "vision_kk",
    "shop_table",
];

const getText = (value, lang = "ru") => {
    if (!value) return "—";
    if (typeof value === "string") return value || "—";
    return value[lang] || value.ru || "—";
};

const formatPrice = (value) => {
    return `${Number(value || 0).toLocaleString("ru-RU")} ₸`;
};

const createEmptyCharacteristic = () => ({
    id: `${Date.now()}-${Math.random()}`,
    label_ru: "",
    label_en: "",
    label_kk: "",
    value: "",
});

const parseShopTableToCharacteristics = (shopTable) => {
    if (!shopTable) {
        return [];
    }

    let parsed = shopTable;

    if (typeof shopTable === "string") {
        try {
            parsed = JSON.parse(shopTable);
        } catch (e) {
            return [];
        }
    }

    const ruList = Array.isArray(parsed.ru) ? parsed.ru : [];
    const enList = Array.isArray(parsed.en) ? parsed.en : [];
    const kkList = Array.isArray(parsed.kk) ? parsed.kk : [];

    const maxLength = Math.max(ruList.length, enList.length, kkList.length);
    const result = [];

    for (let index = 0; index < maxLength; index++) {
        result.push({
            id: `${Date.now()}-${index}-${Math.random()}`,
            label_ru: ruList[index]?.label || ruList[index]?.title || ruList[index]?.name || "",
            label_en: enList[index]?.label || enList[index]?.title || enList[index]?.name || "",
            label_kk: kkList[index]?.label || kkList[index]?.title || kkList[index]?.name || "",
            value: ruList[index]?.value || enList[index]?.value || kkList[index]?.value || "",
        });
    }

    return result;
};

const buildShopTableFromCharacteristics = (characteristics) => {
    const cleanCharacteristics = characteristics.filter((item) => {
        return (
            String(item.label_ru || "").trim() ||
            String(item.label_en || "").trim() ||
            String(item.label_kk || "").trim() ||
            String(item.value || "").trim()
        );
    });

    return {
        ru: cleanCharacteristics.map((item) => ({
            label: String(item.label_ru || "").trim() || "—",
            title: String(item.label_ru || "").trim() || "—",
            name: String(item.label_ru || "").trim() || "—",
            value: String(item.value || "").trim() || "—",
        })),
        en: cleanCharacteristics.map((item) => ({
            label: String(item.label_en || "").trim() || String(item.label_ru || "").trim() || "—",
            title: String(item.label_en || "").trim() || String(item.label_ru || "").trim() || "—",
            name: String(item.label_en || "").trim() || String(item.label_ru || "").trim() || "—",
            value: String(item.value || "").trim() || "—",
        })),
        kk: cleanCharacteristics.map((item) => ({
            label: String(item.label_kk || "").trim() || String(item.label_ru || "").trim() || "—",
            title: String(item.label_kk || "").trim() || String(item.label_ru || "").trim() || "—",
            name: String(item.label_kk || "").trim() || String(item.label_ru || "").trim() || "—",
            value: String(item.value || "").trim() || "—",
        })),
    };
};

const makeInitialForm = (fields) => {
    const nextForm = {};

    Object.keys(fields || {}).forEach((key) => {
        const field = fields[key];

        if (field.type === "file") return;
        if (field.type === "file_multiple") return;
        if (key === "shop_table") return;
        if (key === "gallery") return;
        if (key === "gallery_content_types") return;
        if (key === "gallery_content_type_map") return;
        if (key === "delete_gallery_ids") return;
        if (key === "clear_gallery") return;

        if (field.type === "checkbox") {
            nextForm[key] = Boolean(field.value || field.default || false);
            return;
        }

        nextForm[key] = field.value ?? field.default ?? "";
    });

    return nextForm;
};

const clearMultipartHeaders = (headers) => {
    if (!headers) return;

    if (typeof headers.delete === "function") {
        headers.delete("Content-Type");
        headers.delete("content-type");
    }

    delete headers["Content-Type"];
    delete headers["content-type"];

    if (headers.common) {
        delete headers.common["Content-Type"];
        delete headers.common["content-type"];
    }

    if (headers.post) {
        delete headers.post["Content-Type"];
        delete headers.post["content-type"];
    }
};

const uploadFormData = (url, formData) => {
    return axios.post(url, formData, {
        transformRequest: [
            (data, headers) => {
                clearMultipartHeaders(headers);
                return data;
            },
        ],
    });
};

const getGalleryContentTypeFromFile = (file) => {
    const fileType = String(file?.type || "").toLowerCase();
    const fileName = String(file?.name || "").toLowerCase();

    if (
        fileType.startsWith("video/") ||
        fileName.endsWith(".mp4") ||
        fileName.endsWith(".webm") ||
        fileName.endsWith(".ogg") ||
        fileName.endsWith(".mov") ||
        fileName.endsWith(".m4v")
    ) {
        return "Video";
    }

    return "Photo";
};

const isGalleryVideo = (item) => {
    const contentType = String(item?.content_type || "").toLowerCase();
    const link = String(item?.link || item?.file || item?.preview || "");

    return (
        contentType === "video" ||
        contentType === "video/mp4" ||
        contentType.includes("video") ||
        link.toLowerCase().endsWith(".mp4") ||
        link.toLowerCase().endsWith(".webm") ||
        link.toLowerCase().endsWith(".ogg") ||
        link.toLowerCase().endsWith(".mov") ||
        link.toLowerCase().endsWith(".m4v")
    );
};

const getGalleryLink = (item) => {
    return item?.link || item?.file || item?.url || "";
};

const formatFileSize = (size) => {
    const number = Number(size || 0);

    if (!number) return "—";

    if (number < 1024 * 1024) {
        return `${Math.round(number / 1024)} KB`;
    }

    return `${(number / 1024 / 1024).toFixed(1)} MB`;
};

const createGalleryUploadItem = (file) => {
    return {
        id: `${Date.now()}-${Math.random()}-${file.name}`,
        file,
        preview: URL.createObjectURL(file),
        content_type: getGalleryContentTypeFromFile(file),
        name: file.name,
        size: file.size,
    };
};

const ProductGalleryUploader = ({
                                    modalMode,
                                    existingGallery,
                                    galleryUploads,
                                    galleryActionLoading,
                                    onAddFiles,
                                    onRemoveUpload,
                                    onChangeUploadType,
                                    onDeleteExisting,
                                    onClearGallery,
                                    onChangeExistingType,
                                }) => {
    const [dragActive, setDragActive] = useState(false);

    const handleFiles = (files) => {
        const list = Array.from(files || []);

        if (list.length === 0) return;

        onAddFiles(list);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();

        setDragActive(false);
        handleFiles(event.dataTransfer.files);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();

        setDragActive(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();

        setDragActive(false);
    };

    return (
        <div className="product-gallery-uploader">
            <div className="product-gallery-uploader-head">
                <div>
                    <h3>Галерея товара</h3>
                    <p>Фото и видео для карточки товара. Новые файлы сохраняются multipart/form-data.</p>
                </div>

                {modalMode === "edit" && existingGallery.length > 0 && (
                    <button
                        className="product-gallery-clear-button"
                        type="button"
                        disabled={galleryActionLoading}
                        onClick={onClearGallery}
                    >
                        {galleryActionLoading ? "Обновляем..." : "Очистить галерею"}
                    </button>
                )}
            </div>

            {modalMode === "create" && (
                <div className="product-gallery-info">
                    Галерея будет загружена сразу при создании товара. Каждый файл уйдёт отдельным multipart-полем <b>gallery</b>.
                </div>
            )}

            {modalMode === "edit" && existingGallery.length > 0 && (
                <div className="product-gallery-existing">
                    <div className="product-gallery-section-title">
                        <strong>Текущая галерея</strong>
                        <span>{existingGallery.length}</span>
                    </div>

                    <div className="product-gallery-grid">
                        {existingGallery.map((item) => {
                            const link = getGalleryLink(item);
                            const isVideo = isGalleryVideo(item);
                            const contentType = item.content_type || "Photo";

                            return (
                                <div className="product-gallery-card" key={item.id}>
                                    <div className="product-gallery-preview">
                                        {link ? (
                                            isVideo ? (
                                                <video src={link} controls preload="metadata" />
                                            ) : (
                                                <img src={link} alt="" />
                                            )
                                        ) : (
                                            <div className="product-gallery-no-preview">
                                                Нет файла
                                            </div>
                                        )}

                                        <span>{isVideo ? "Video" : "Photo"}</span>
                                    </div>

                                    <div className="product-gallery-card-body">
                                        <div>
                                            <strong>Файл #{item.id}</strong>
                                            <small title={link}>{link || "—"}</small>
                                        </div>

                                        <select
                                            value={contentType}
                                            disabled={galleryActionLoading}
                                            onChange={(e) => onChangeExistingType(item.id, e.target.value)}
                                        >
                                            <option value="Photo">Photo</option>
                                            <option value="Video">Video</option>
                                        </select>

                                        <div className="product-gallery-card-actions">
                                            {link && (
                                                <a href={link} target="_blank" rel="noreferrer">
                                                    Открыть
                                                </a>
                                            )}

                                            <button
                                                type="button"
                                                disabled={galleryActionLoading}
                                                onClick={() => onDeleteExisting(item)}
                                            >
                                                Удалить
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {modalMode === "edit" && existingGallery.length === 0 && (
                <div className="product-gallery-empty">
                    Текущая галерея пустая.
                </div>
            )}

            <div
                className={[
                    "product-gallery-dropzone",
                    dragActive ? "product-gallery-dropzone-active" : "",
                ].join(" ")}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                <input
                    id="product-gallery-input"
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={(e) => {
                        handleFiles(e.target.files);
                        e.target.value = "";
                    }}
                />

                <label htmlFor="product-gallery-input">
                    <span className="product-gallery-dropzone-icon">+</span>
                    <strong>Добавить новые фото или видео</strong>
                    <small>Перетащи файлы сюда или нажми для выбора</small>
                    <em>Можно выбрать сразу несколько файлов</em>
                </label>
            </div>

            {galleryUploads.length > 0 && (
                <div className="product-gallery-new">
                    <div className="product-gallery-section-title">
                        <strong>Новые файлы к загрузке</strong>
                        <span>{galleryUploads.length}</span>
                    </div>

                    <div className="product-gallery-grid">
                        {galleryUploads.map((item, index) => {
                            const isVideo = item.content_type === "Video";

                            return (
                                <div className="product-gallery-card" key={item.id}>
                                    <div className="product-gallery-preview">
                                        {isVideo ? (
                                            <video src={item.preview} controls preload="metadata" />
                                        ) : (
                                            <img src={item.preview} alt="" />
                                        )}

                                        <span>{isVideo ? "Video" : "Photo"}</span>
                                    </div>

                                    <div className="product-gallery-card-body">
                                        <div>
                                            <strong>Новый файл #{index + 1}</strong>
                                            <small title={item.name}>{item.name}</small>
                                            <small>{formatFileSize(item.size)}</small>
                                        </div>

                                        <select
                                            value={item.content_type}
                                            onChange={(e) => onChangeUploadType(item.id, e.target.value)}
                                        >
                                            <option value="Photo">Photo</option>
                                            <option value="Video">Video</option>
                                        </select>

                                        <button
                                            type="button"
                                            onClick={() => onRemoveUpload(item.id)}
                                        >
                                            Убрать из загрузки
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="product-gallery-info">
                        Эти файлы будут отправлены после нажатия <b>Сохранить товар</b>.
                    </div>
                </div>
            )}
        </div>
    );
};

export const ProductsPage = () => {
    const [items, setItems] = useState([]);
    const [filters, setFilters] = useState(null);
    const [count, setCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [nextOffset, setNextOffset] = useState(null);

    const [q, setQ] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [subCategoryId, setSubCategoryId] = useState("");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [ordering, setOrdering] = useState("-id");
    const [limit, setLimit] = useState(20);

    const [filtersOpen, setFiltersOpen] = useState(false);

    const [loading, setLoading] = useState(true);
    const [moreLoading, setMoreLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("");
    const [modalTitle, setModalTitle] = useState("");
    const [modalFields, setModalFields] = useState({});
    const [modalForm, setModalForm] = useState({});
    const [modalFile, setModalFile] = useState(null);
    const [modalSaving, setModalSaving] = useState(false);
    const [modalError, setModalError] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [itemFiles, setItemFiles] = useState([]);
    const [characteristics, setCharacteristics] = useState([]);

    const [existingGallery, setExistingGallery] = useState([]);
    const [galleryUploads, setGalleryUploads] = useState([]);
    const [galleryActionLoading, setGalleryActionLoading] = useState(false);

    const [fileForm, setFileForm] = useState({
        position: 0,
        ru_name: "",
        name_ru: "",
        name_en: "",
        name_kk: "",
    });
    const [documentFile, setDocumentFile] = useState(null);
    const [fileSaving, setFileSaving] = useState(false);
    const [fileError, setFileError] = useState("");

    const [deleteModal, setDeleteModal] = useState({
        open: false,
        type: "",
        id: null,
        title: "",
        text: "",
        loading: false,
        payload: null,
    });

    const revokeGalleryUploadPreviews = (uploads) => {
        uploads.forEach((item) => {
            if (item.preview) {
                URL.revokeObjectURL(item.preview);
            }
        });
    };

    const resetGalleryUploads = () => {
        revokeGalleryUploadPreviews(galleryUploads);
        setGalleryUploads([]);
    };

    const resetGalleryState = () => {
        revokeGalleryUploadPreviews(galleryUploads);
        setExistingGallery([]);
        setGalleryUploads([]);
        setGalleryActionLoading(false);
    };

    const addGalleryFiles = (files) => {
        const nextFiles = files.map((file) => createGalleryUploadItem(file));

        setGalleryUploads((prev) => [...prev, ...nextFiles]);
    };

    const removeGalleryUpload = (uploadId) => {
        setGalleryUploads((prev) => {
            const item = prev.find((upload) => upload.id === uploadId);

            if (item?.preview) {
                URL.revokeObjectURL(item.preview);
            }

            return prev.filter((upload) => upload.id !== uploadId);
        });
    };

    const changeGalleryUploadType = (uploadId, contentType) => {
        setGalleryUploads((prev) =>
            prev.map((item) => {
                if (item.id !== uploadId) return item;

                return {
                    ...item,
                    content_type: contentType,
                };
            })
        );
    };

    const appendGalleryMultipart = (formData) => {
        galleryUploads.forEach((item, index) => {
            formData.append("gallery", item.file, item.file.name);
            formData.append("gallery_content_types", item.content_type);
            formData.append(`gallery_content_type_${index}`, item.content_type);
        });
    };

    const loadItems = async (reset = true) => {
        if (reset) {
            setLoading(true);
        } else {
            setMoreLoading(true);
        }

        setError("");
        setMessage("");

        const offset = reset ? 0 : nextOffset;

        const params = {
            limit,
            offset: offset || 0,
            ordering,
        };

        if (q.trim()) params.q = q.trim();
        if (categoryId) params.category_id = categoryId;
        if (subCategoryId) params.sub_category_id = subCategoryId;
        if (minPrice) params.min_price = minPrice;
        if (maxPrice) params.max_price = maxPrice;

        try {
            const response = await axios.get(ITEMS_URL, { params });
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось загрузить товары");
                return;
            }

            if (reset) {
                setItems(data.results || []);
            } else {
                setItems((prev) => [...prev, ...(data.results || [])]);
            }

            setFilters(data.filters || null);
            setCount(data.count || 0);
            setHasMore(Boolean(data.has_more));
            setNextOffset(data.next_offset);
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setLoading(false);
            setMoreLoading(false);
        }
    };

    const openCreateItem = async () => {
        setModalMode("create");
        setModalTitle("Добавить товар");
        setEditingId(null);
        setEditingItem(null);
        setItemFiles([]);
        setModalFields({});
        setModalForm({});
        setModalFile(null);
        setCharacteristics([]);
        setModalError("");
        resetGalleryState();
        setModalOpen(true);

        try {
            const response = await axios.get(ITEM_CREATE_URL);
            const data = response.data;

            if (!data.success) {
                setModalError(data.message || "Не удалось получить поля товара");
                return;
            }

            setModalFields(data.fields || {});
            setModalForm(makeInitialForm(data.fields || {}));

            const shopTableValue = data.fields?.shop_table?.value || data.fields?.shop_table?.default || null;
            setCharacteristics(parseShopTableToCharacteristics(shopTableValue));
        } catch (e) {
            setModalError(e.response?.data?.message || "Ошибка соединения с сервером");
        }
    };

    const loadItemForEdit = async (itemId) => {
        try {
            const response = await axios.get(ITEM_UPDATE_URL(itemId));
            const data = response.data;

            if (!data.success) {
                setModalError(data.message || "Не удалось получить данные товара");
                return;
            }

            const gallery = data.item?.gallery || data.gallery || [];

            setModalFields(data.fields || {});
            setModalForm(makeInitialForm(data.fields || {}));
            setEditingItem(data.item || null);
            setItemFiles(data.item?.files || []);
            setExistingGallery(gallery);

            const shopTableValue = data.fields?.shop_table?.value || data.item?.shop_table || null;
            setCharacteristics(parseShopTableToCharacteristics(shopTableValue));
        } catch (e) {
            setModalError(e.response?.data?.message || "Ошибка соединения с сервером");
        }
    };

    const openEditItem = async (itemId) => {
        setModalMode("edit");
        setModalTitle("Редактировать товар");
        setEditingId(itemId);
        setEditingItem(null);
        setItemFiles([]);
        setModalFields({});
        setModalForm({});
        setModalFile(null);
        setCharacteristics([]);
        setModalError("");
        setFileError("");
        resetGalleryState();
        setModalOpen(true);

        await loadItemForEdit(itemId);
    };

    const closeModal = () => {
        setModalOpen(false);
        setModalMode("");
        setModalTitle("");
        setModalFields({});
        setModalForm({});
        setModalFile(null);
        setCharacteristics([]);
        setModalError("");
        setEditingId(null);
        setEditingItem(null);
        setItemFiles([]);
        setFileError("");
        setDocumentFile(null);
        setFileForm({
            position: 0,
            ru_name: "",
            name_ru: "",
            name_en: "",
            name_kk: "",
        });
        resetGalleryState();
    };

    const changeModalField = (key, value) => {
        setModalForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const addCharacteristic = () => {
        setCharacteristics((prev) => [...prev, createEmptyCharacteristic()]);
    };

    const removeCharacteristic = (id) => {
        setCharacteristics((prev) => prev.filter((item) => item.id !== id));
    };

    const changeCharacteristic = (id, key, value) => {
        setCharacteristics((prev) =>
            prev.map((item) => {
                if (item.id !== id) {
                    return item;
                }

                return {
                    ...item,
                    [key]: value,
                };
            })
        );
    };

    const submitItem = async (e) => {
        e.preventDefault();

        if (modalSaving) return;

        setModalSaving(true);
        setModalError("");
        setMessage("");

        try {
            const formData = new FormData();

            Object.keys(modalForm).forEach((key) => {
                if (key === "shop_table") return;

                const value = modalForm[key];

                if (typeof value === "boolean") {
                    formData.append(key, value ? "true" : "false");
                } else {
                    formData.append(key, value ?? "");
                }
            });

            formData.append(
                "shop_table",
                JSON.stringify(buildShopTableFromCharacteristics(characteristics))
            );

            if (modalFile) {
                formData.append("image", modalFile, modalFile.name);
            }

            appendGalleryMultipart(formData);

            const url = modalMode === "edit" ? ITEM_UPDATE_URL(editingId) : ITEM_CREATE_URL;

            const response = await uploadFormData(url, formData);
            const data = response.data;

            if (!data.success) {
                setModalError(data.message || "Не удалось сохранить товар");
                return;
            }

            setMessage(data.message || "Товар сохранён");

            if (modalMode === "edit" && editingId) {
                resetGalleryUploads();
                await loadItemForEdit(editingId);
                await loadItems(true);
            } else {
                closeModal();
                await loadItems(true);
            }
        } catch (e) {
            setModalError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setModalSaving(false);
        }
    };

    const deleteGalleryItem = async (galleryItem) => {
        if (!editingId || galleryActionLoading) return;

        setGalleryActionLoading(true);
        setModalError("");

        try {
            const formData = new FormData();

            formData.append("delete_gallery_ids", String(galleryItem.id));

            const response = await uploadFormData(ITEM_UPDATE_URL(editingId), formData);
            const data = response.data;

            if (!data.success) {
                setModalError(data.message || "Не удалось удалить файл галереи");
                return;
            }

            setMessage(data.message || "Файл галереи удалён");
            await loadItemForEdit(editingId);
        } catch (e) {
            setModalError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setGalleryActionLoading(false);
        }
    };

    const clearGallery = async () => {
        if (!editingId || galleryActionLoading) return;

        setGalleryActionLoading(true);
        setModalError("");

        try {
            const formData = new FormData();

            formData.append("clear_gallery", "true");

            const response = await uploadFormData(ITEM_UPDATE_URL(editingId), formData);
            const data = response.data;

            if (!data.success) {
                setModalError(data.message || "Не удалось очистить галерею");
                return;
            }

            setMessage(data.message || "Галерея очищена");
            await loadItemForEdit(editingId);
        } catch (e) {
            setModalError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setGalleryActionLoading(false);
        }
    };

    const changeExistingGalleryType = async (galleryId, contentType) => {
        if (!editingId || galleryActionLoading) return;

        setGalleryActionLoading(true);
        setModalError("");

        try {
            const formData = new FormData();

            formData.append(
                "gallery_content_type_map",
                JSON.stringify({
                    [galleryId]: contentType,
                })
            );

            const response = await uploadFormData(ITEM_UPDATE_URL(editingId), formData);
            const data = response.data;

            if (!data.success) {
                setModalError(data.message || "Не удалось изменить тип файла");
                return;
            }

            await loadItemForEdit(editingId);
        } catch (e) {
            setModalError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setGalleryActionLoading(false);
        }
    };

    const addItemFile = async (e) => {
        e.preventDefault();

        if (!editingId || fileSaving) return;

        setFileSaving(true);
        setFileError("");

        try {
            const formData = new FormData();

            formData.append("position", fileForm.position || 0);
            formData.append("ru_name", fileForm.ru_name || "");
            formData.append("name_ru", fileForm.name_ru || "");
            formData.append("name_en", fileForm.name_en || "");
            formData.append("name_kk", fileForm.name_kk || "");

            if (documentFile) {
                formData.append("document", documentFile, documentFile.name);
            }

            const response = await uploadFormData(ITEM_FILE_CREATE_URL(editingId), formData);
            const data = response.data;

            if (!data.success) {
                setFileError(data.message || "Не удалось добавить файл");
                return;
            }

            setFileForm({
                position: 0,
                ru_name: "",
                name_ru: "",
                name_en: "",
                name_kk: "",
            });
            setDocumentFile(null);

            await loadItemForEdit(editingId);
        } catch (e) {
            setFileError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setFileSaving(false);
        }
    };

    const openDeleteItemModal = (item) => {
        setDeleteModal({
            open: true,
            type: "item",
            id: item.id,
            title: "Удалить товар?",
            text: `Товар “${item.ru_name || item.name?.ru || "без названия"}” будет удалён. Это действие нельзя случайно отменить.`,
            loading: false,
            payload: null,
        });
    };

    const openDeleteFileModal = (file) => {
        setDeleteModal({
            open: true,
            type: "file",
            id: file.id,
            title: "Удалить файл?",
            text: `Файл “${file.ru_name || file.name?.ru || "без названия"}” будет удалён из товара.`,
            loading: false,
            payload: null,
        });
    };

    const openDeleteGalleryModal = (galleryItem) => {
        setDeleteModal({
            open: true,
            type: "gallery",
            id: galleryItem.id,
            title: "Удалить файл галереи?",
            text: `Файл галереи #${galleryItem.id} будет удалён из товара.`,
            loading: false,
            payload: galleryItem,
        });
    };

    const closeDeleteModal = () => {
        if (deleteModal.loading) return;

        setDeleteModal({
            open: false,
            type: "",
            id: null,
            title: "",
            text: "",
            loading: false,
            payload: null,
        });
    };

    const confirmDelete = async () => {
        if (!deleteModal.id || deleteModal.loading) return;

        setDeleteModal((prev) => ({
            ...prev,
            loading: true,
        }));

        setError("");
        setMessage("");
        setModalError("");

        try {
            let response;

            if (deleteModal.type === "item") {
                response = await axios.post(ITEM_DELETE_URL(deleteModal.id));
            }

            if (deleteModal.type === "file") {
                response = await axios.post(ITEM_FILE_DELETE_URL(deleteModal.id));
            }

            if (deleteModal.type === "gallery") {
                const formData = new FormData();

                formData.append("delete_gallery_ids", String(deleteModal.id));

                response = await uploadFormData(ITEM_UPDATE_URL(editingId), formData);
            }

            const data = response.data;

            if (!data.success) {
                if (deleteModal.type === "item") {
                    setError(data.message || "Не удалось удалить товар");
                } else {
                    setModalError(data.message || "Не удалось удалить");
                }

                return;
            }

            setMessage(data.message || "Удалено");

            const deletedType = deleteModal.type;

            closeDeleteModal();

            if (deletedType === "item") {
                await loadItems(true);
            }

            if ((deletedType === "file" || deletedType === "gallery") && editingId) {
                await loadItemForEdit(editingId);
            }
        } catch (e) {
            if (deleteModal.type === "item") {
                setError(e.response?.data?.message || "Ошибка соединения с сервером");
            } else {
                setModalError(e.response?.data?.message || "Ошибка соединения с сервером");
            }

            setDeleteModal((prev) => ({
                ...prev,
                loading: false,
            }));
        }
    };

    const resetFilters = () => {
        setQ("");
        setCategoryId("");
        setSubCategoryId("");
        setMinPrice("");
        setMaxPrice("");
        setOrdering("-id");
        setLimit(20);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            loadItems(true);
        }, 350);

        return () => clearTimeout(timer);
    }, [q, categoryId, subCategoryId, minPrice, maxPrice, ordering, limit]);

    const renderField = (key) => {
        const field = modalFields[key];

        if (!field) return null;

        if (key === "shop_table") {
            return (
                <div className="product-characteristics" key={key}>
                    <div className="product-characteristics-head">
                        <div>
                            <h3>Характеристики</h3>
                            <p>Название характеристики на RU / EN / KK и общее значение</p>
                        </div>

                        <button type="button" onClick={addCharacteristic}>
                            + Характеристика
                        </button>
                    </div>

                    {characteristics.length === 0 && (
                        <div className="product-characteristics-empty">
                            Характеристик пока нет
                        </div>
                    )}

                    {characteristics.length > 0 && (
                        <div className="product-characteristics-list">
                            {characteristics.map((item, index) => (
                                <div className="product-characteristic-card" key={item.id}>
                                    <div className="product-characteristic-number">
                                        #{index + 1}
                                    </div>

                                    <label>
                                        <span>Название RU</span>
                                        <input
                                            value={item.label_ru}
                                            onChange={(e) =>
                                                changeCharacteristic(item.id, "label_ru", e.target.value)
                                            }
                                            placeholder="Мощность"
                                        />
                                    </label>

                                    <label>
                                        <span>Название EN</span>
                                        <input
                                            value={item.label_en}
                                            onChange={(e) =>
                                                changeCharacteristic(item.id, "label_en", e.target.value)
                                            }
                                            placeholder="Power"
                                        />
                                    </label>

                                    <label>
                                        <span>Название KK</span>
                                        <input
                                            value={item.label_kk}
                                            onChange={(e) =>
                                                changeCharacteristic(item.id, "label_kk", e.target.value)
                                            }
                                            placeholder="Қуаты"
                                        />
                                    </label>

                                    <label>
                                        <span>Значение</span>
                                        <input
                                            value={item.value}
                                            onChange={(e) =>
                                                changeCharacteristic(item.id, "value", e.target.value)
                                            }
                                            placeholder="60W"
                                        />
                                    </label>

                                    <button
                                        className="product-characteristic-delete"
                                        type="button"
                                        onClick={() => removeCharacteristic(item.id)}
                                    >
                                        Удалить
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        if (field.type === "file") {
            return (
                <label className="product-form-field" key={key}>
                    <span>{field.label}</span>

                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setModalFile(e.target.files?.[0] || null)}
                    />

                    {field.value && field.value !== "—" && (
                        <small>Текущая картинка: есть</small>
                    )}
                </label>
            );
        }

        if (field.type === "checkbox") {
            return (
                <label className="product-form-check" key={key}>
                    <input
                        type="checkbox"
                        checked={Boolean(modalForm[key])}
                        onChange={(e) => changeModalField(key, e.target.checked)}
                    />

                    <span>{field.label}</span>
                </label>
            );
        }

        if (field.type === "select") {
            return (
                <label className="product-form-field" key={key}>
                    <span>
                        {field.label}
                        {field.required && <b>*</b>}
                    </span>

                    <select
                        value={modalForm[key] || ""}
                        onChange={(e) => changeModalField(key, e.target.value)}
                    >
                        <option value="">Выберите</option>

                        {(field.options || []).map((item) => (
                            <option key={item.value} value={item.value}>
                                {item.label}
                            </option>
                        ))}
                    </select>
                </label>
            );
        }

        if (field.type === "textarea" || field.type === "json") {
            return (
                <label className="product-form-field product-form-field-full" key={key}>
                    <span>
                        {field.label}
                        {field.required && <b>*</b>}
                    </span>

                    <textarea
                        value={modalForm[key] || ""}
                        onChange={(e) => changeModalField(key, e.target.value)}
                        placeholder={field.type === "json" ? "{...}" : field.placeholder || ""}
                    />
                </label>
            );
        }

        return (
            <label className="product-form-field" key={key}>
                <span>
                    {field.label}
                    {field.required && <b>*</b>}
                </span>

                <input
                    type={field.type || "text"}
                    value={modalForm[key] || ""}
                    onChange={(e) => changeModalField(key, e.target.value)}
                    placeholder={field.placeholder || ""}
                />
            </label>
        );
    };

    const filtersContent = (
        <div className="products-filter-panel">
            <div className="products-filter-field products-filter-search">
                <span>Поиск</span>
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={filters?.q?.placeholder || "Название, slug, описание"}
                />
            </div>

            <div className="products-filter-field">
                <span>Категория</span>
                <select
                    value={categoryId}
                    onChange={(e) => {
                        setCategoryId(e.target.value);
                        setSubCategoryId("");
                    }}
                >
                    <option value="">Все</option>

                    {filters?.category_id?.options?.map((item) => (
                        <option key={item.value} value={item.value}>
                            {item.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="products-filter-field">
                <span>Подкатегория</span>
                <select value={subCategoryId} onChange={(e) => setSubCategoryId(e.target.value)}>
                    <option value="">Все</option>

                    {filters?.sub_category_id?.options?.map((item) => (
                        <option key={item.value} value={item.value}>
                            {item.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="products-filter-field">
                <span>Цена от</span>
                <input value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
            </div>

            <div className="products-filter-field">
                <span>Цена до</span>
                <input value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
            </div>

            <div className="products-filter-field">
                <span>Сортировка</span>
                <select value={ordering} onChange={(e) => setOrdering(e.target.value)}>
                    {filters?.ordering?.options?.map((item) => (
                        <option key={item.value} value={item.value}>
                            {item.label}
                        </option>
                    ))}

                    {!filters?.ordering?.options && (
                        <>
                            <option value="-id">Сначала новые</option>
                            <option value="id">Сначала старые</option>
                        </>
                    )}
                </select>
            </div>

            <div className="products-filter-field products-filter-limit">
                <span>Показать</span>
                <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </select>
            </div>

            <button className="products-filter-reset" type="button" onClick={resetFilters}>
                Сбросить
            </button>
        </div>
    );

    return (
        <section className="products-page">
            <div className="products-head">
                <div>
                    <h1>Товары</h1>
                    <p>Позиции магазина, цены, картинки, описания, галерея и файлы</p>
                </div>

                <button className="products-create-btn" type="button" onClick={openCreateItem}>
                    + Добавить товар
                </button>
            </div>

            <div className="products-desktop-filters">{filtersContent}</div>

            <div className="products-mobile-filter-row">
                <button type="button" onClick={() => setFiltersOpen(true)}>
                    Фильтры
                </button>

                <span>{count} товаров</span>
            </div>

            {filtersOpen && (
                <div className="products-filter-modal">
                    <button
                        className="products-filter-modal-bg"
                        type="button"
                        onClick={() => setFiltersOpen(false)}
                    />

                    <div className="products-filter-modal-card">
                        <div className="products-filter-modal-head">
                            <div>
                                <h2>Фильтры</h2>
                                <p>Поиск, категории, цена и сортировка</p>
                            </div>

                            <button type="button" onClick={() => setFiltersOpen(false)}>
                                ✕
                            </button>
                        </div>

                        {filtersContent}

                        <button
                            className="products-filter-apply"
                            type="button"
                            onClick={() => setFiltersOpen(false)}
                        >
                            Применить
                        </button>
                    </div>
                </div>
            )}

            {error && <div className="products-error">{error}</div>}
            {message && <div className="products-success">{message}</div>}

            <div className="products-list-head">
                <strong>Всего товаров: {count}</strong>
                {loading && <span>Загрузка...</span>}
            </div>

            {loading && <div className="products-loading">Загрузка товаров...</div>}

            {!loading && items.length === 0 && (
                <div className="products-empty">Товары не найдены</div>
            )}

            {!loading && items.length > 0 && (
                <div className="products-list">
                    {items.map((item) => (
                        <article className="product-card" key={item.id}>
                            <div className="product-card-image">
                                {item.image && item.image !== "—" ? (
                                    <img src={item.image} alt="" />
                                ) : (
                                    <span>IMG</span>
                                )}
                            </div>

                            <div className="product-card-content">
                                <div className="product-card-main">
                                    <div>
                                        <span>Товар #{item.id}</span>
                                        <h2>{item.ru_name || getText(item.name) || "—"}</h2>
                                        <p>{getText(item.short_description)}</p>
                                    </div>

                                    <div className="product-card-price">
                                        <span>Цена</span>
                                        <strong>{formatPrice(item.price)}</strong>
                                        <small>Опт: {formatPrice(item.opt_price)}</small>
                                    </div>
                                </div>

                                <div className="product-card-grid">
                                    <div>
                                        <span>Категория</span>
                                        <strong>{item.category?.ru_name || "—"}</strong>
                                    </div>

                                    <div>
                                        <span>Подкатегория</span>
                                        <strong>{item.sub_category?.ru_name || "—"}</strong>
                                    </div>

                                    <div>
                                        <span>Slug</span>
                                        <strong>{item.slug || "—"}</strong>
                                    </div>

                                    <div>
                                        <span>Файлы</span>
                                        <strong>{item.files_count || 0}</strong>
                                    </div>
                                </div>

                                <div className="product-card-actions">
                                    <button type="button" onClick={() => openEditItem(item.id)}>
                                        Подробнее / изменить
                                    </button>

                                    <button type="button" onClick={() => openDeleteItemModal(item)}>
                                        Удалить
                                    </button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {hasMore && (
                <div className="products-more">
                    <button type="button" onClick={() => loadItems(false)} disabled={moreLoading}>
                        {moreLoading ? "Загрузка..." : "Показать ещё"}
                    </button>
                </div>
            )}

            {modalOpen && (
                <div className="product-modal">
                    <button className="product-modal-bg" type="button" onClick={closeModal} />

                    <div className="product-modal-card">
                        <div className="product-modal-head">
                            <div>
                                <h2>{modalTitle}</h2>
                                <p>Данные товара на RU / EN / KK</p>
                            </div>

                            <button type="button" onClick={closeModal}>
                                ✕
                            </button>
                        </div>

                        {modalError && <div className="products-error">{modalError}</div>}

                        {editingItem && (
                            <div className="product-current-preview">
                                <div className="product-current-image">
                                    {editingItem.image && editingItem.image !== "—" ? (
                                        <img src={editingItem.image} alt="" />
                                    ) : (
                                        <span>IMG</span>
                                    )}
                                </div>

                                <div>
                                    <span>Текущий товар</span>
                                    <strong>{editingItem.ru_name || "—"}</strong>
                                    <p>{editingItem.category?.ru_name || "—"} / {editingItem.sub_category?.ru_name || "—"}</p>
                                </div>
                            </div>
                        )}

                        <form className="product-form" onSubmit={submitItem}>
                            {itemFieldOrder.map((key) => renderField(key))}

                            <ProductGalleryUploader
                                modalMode={modalMode}
                                existingGallery={existingGallery}
                                galleryUploads={galleryUploads}
                                galleryActionLoading={galleryActionLoading}
                                onAddFiles={addGalleryFiles}
                                onRemoveUpload={removeGalleryUpload}
                                onChangeUploadType={changeGalleryUploadType}
                                onDeleteExisting={openDeleteGalleryModal}
                                onClearGallery={clearGallery}
                                onChangeExistingType={changeExistingGalleryType}
                            />

                            <div className="product-form-actions">
                                <button type="button" onClick={closeModal}>
                                    Отмена
                                </button>

                                <button type="submit" disabled={modalSaving}>
                                    {modalSaving ? "Сохраняем..." : "Сохранить товар"}
                                </button>
                            </div>
                        </form>

                        {modalMode === "edit" && (
                            <div className="product-files-section">
                                <div className="product-files-head">
                                    <div>
                                        <h3>Файлы товара</h3>
                                        <p>Документы, инструкции, описания</p>
                                    </div>
                                </div>

                                {fileError && <div className="products-error">{fileError}</div>}

                                <form className="product-file-form" onSubmit={addItemFile}>
                                    <label>
                                        <span>Позиция</span>
                                        <input
                                            type="number"
                                            value={fileForm.position}
                                            onChange={(e) =>
                                                setFileForm((prev) => ({
                                                    ...prev,
                                                    position: e.target.value,
                                                }))
                                            }
                                        />
                                    </label>

                                    <label>
                                        <span>Название для системы</span>
                                        <input
                                            value={fileForm.ru_name}
                                            onChange={(e) =>
                                                setFileForm((prev) => ({
                                                    ...prev,
                                                    ru_name: e.target.value,
                                                }))
                                            }
                                        />
                                    </label>

                                    <label>
                                        <span>Название RU</span>
                                        <input
                                            value={fileForm.name_ru}
                                            onChange={(e) =>
                                                setFileForm((prev) => ({
                                                    ...prev,
                                                    name_ru: e.target.value,
                                                }))
                                            }
                                        />
                                    </label>

                                    <label>
                                        <span>Название EN</span>
                                        <input
                                            value={fileForm.name_en}
                                            onChange={(e) =>
                                                setFileForm((prev) => ({
                                                    ...prev,
                                                    name_en: e.target.value,
                                                }))
                                            }
                                        />
                                    </label>

                                    <label>
                                        <span>Название KK</span>
                                        <input
                                            value={fileForm.name_kk}
                                            onChange={(e) =>
                                                setFileForm((prev) => ({
                                                    ...prev,
                                                    name_kk: e.target.value,
                                                }))
                                            }
                                        />
                                    </label>

                                    <label>
                                        <span>Документ</span>
                                        <input
                                            type="file"
                                            onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
                                        />
                                    </label>

                                    <button type="submit" disabled={fileSaving}>
                                        {fileSaving ? "Добавляем..." : "+ Добавить файл"}
                                    </button>
                                </form>

                                {itemFiles.length === 0 && (
                                    <div className="product-files-empty">Файлов пока нет</div>
                                )}

                                {itemFiles.length > 0 && (
                                    <div className="product-files-list">
                                        {itemFiles.map((file) => (
                                            <div className="product-file-card" key={file.id}>
                                                <div>
                                                    <span>Файл #{file.id}</span>
                                                    <strong>{file.ru_name || getText(file.name) || "—"}</strong>
                                                    <p>Позиция: {file.position}</p>
                                                </div>

                                                <div className="product-file-actions">
                                                    {file.document && file.document !== "—" && (
                                                        <a href={file.document} target="_blank" rel="noreferrer">
                                                            Открыть
                                                        </a>
                                                    )}

                                                    <button type="button" onClick={() => openDeleteFileModal(file)}>
                                                        Удалить
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {deleteModal.open && (
                <div className="delete-modal">
                    <button className="delete-modal-bg" type="button" onClick={closeDeleteModal} />

                    <div className="delete-modal-card">
                        <h2>{deleteModal.title}</h2>
                        <p>{deleteModal.text}</p>

                        <div className="delete-modal-actions">
                            <button type="button" onClick={closeDeleteModal}>
                                Отмена
                            </button>

                            <button type="button" onClick={confirmDelete} disabled={deleteModal.loading}>
                                {deleteModal.loading ? "Удаляем..." : "Да, удалить"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};
